import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiInfo, FiMessageSquare, FiPlus, FiRefreshCw, FiSearch, FiUploadCloud, FiX } from "react-icons/fi";
import axios from "../api/axiosInstance";

const LANGUAGE_OPTIONS = [
  ["en", "English"],
  ["es", "Spanish"],
  ["fr", "French"],
  ["it", "Italian"],
  ["de", "German"],
  ["pt", "Portuguese"],
  ["nl", "Dutch"],
];

const TRIP_TYPE_OPTIONS = [
  ["business", "Business"],
  ["vacation", "Leisure"],
  ["bleisure", "Bleisure"],
  ["family", "Family trip"],
  ["couple", "Couple"],
  ["group", "Group"],
  ["solo", "Solo"],
];

const TRAVEL_GROUP_OPTIONS = [
  ["solo", "Solo traveler"],
  ["couple", "Couple"],
  ["family", "Family"],
  ["friends", "Friends"],
  ["business", "Business traveler"],
  ["group", "Group"],
];

const EMPTY_IMPORT_FORM = { source_id: "", file: null };
const EMPTY_AI_EXTRACTION_FORM = { source_id: "", raw_text: "" };
const EMPTY_MANUAL_FORM = {
  source_id: "",
  external_review_id: "",
  external_reservation_id: "",
  guest_name: "",
  author_location: "",
  original_language: "en",
  review_date: "",
  published_at: "",
  stay_date_from: "",
  stay_date_to: "",
  date_of_stay_text: "",
  trip_type: "",
  travel_group: "",
  title: "",
  positive_text: "",
  negative_text: "",
  review_text: "",
  property_reply_text: "",
  property_reply_date: "",
  rating_value: "",
  rating_scale_max: "10",
  raw_payload_text: "",
};

function buildManualForm(sources = []) {
  return {
    ...EMPTY_MANUAL_FORM,
    source_id: sources[0]?.id ? String(sources[0].id) : "",
    review_date: new Date().toISOString().slice(0, 10),
  };
}

function buildAiExtractionForm(sources = []) {
  return {
    ...EMPTY_AI_EXTRACTION_FORM,
    source_id: sources[0]?.id ? String(sources[0].id) : "",
  };
}

function normalizeText(value) {
  return String(value || "").trim();
}

function calculateNormalizedRating100(value, scale) {
  const ratingValue = Number(value);
  const ratingScale = Number(scale);
  if (!Number.isFinite(ratingValue) || !Number.isFinite(ratingScale) || ratingScale <= 0) {
    return null;
  }
  return Math.max(0, Math.min(100, Number(((ratingValue / ratingScale) * 100).toFixed(2))));
}

function formatDate(value) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatScore(review) {
  const value = Number(review?.rating_value);
  const scale = Number(review?.rating_scale_max);
  if (!Number.isFinite(value) || !Number.isFinite(scale) || scale <= 0) return "No score";
  return `${value}/${scale}`;
}

function buildReviewExcerpt(review) {
  const firstFilled = [review?.title, review?.review_text, review?.negative_text, review?.positive_text].find((value) => normalizeText(value));
  if (!firstFilled) return "No comment provided.";
  const normalized = normalizeText(firstFilled).replace(/\s+/g, " ");
  return normalized.length > 160 ? `${normalized.slice(0, 157)}...` : normalized;
}

function getReplyLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded": return "Already answered";
    case "recommended": return "Recommended";
    case "draft_ready": return "Draft ready";
    case "dismissed": return "Dismissed";
    default: return "Pending";
  }
}

function getReplyTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded": return "success";
    case "recommended":
    case "draft_ready": return "warning";
    case "dismissed": return "muted";
    default: return "neutral";
  }
}

function getAnalysisLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "done": return "Ready";
    case "processing": return "In progress";
    case "error": return "Needs review";
    default: return "Waiting";
  }
}

function getAnalysisTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "done": return "success";
    case "processing": return "warning";
    case "error": return "danger";
    default: return "neutral";
  }
}

function getImportRunLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "done": return "Completed";
    case "partial": return "Completed with issues";
    case "processing": return "Processing";
    case "error": return "Needs review";
    default: return "Pending";
  }
}

function getImportRunTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "done": return "success";
    case "partial":
    case "processing": return "warning";
    case "error": return "danger";
    default: return "neutral";
  }
}

function parseFileNameFromDisposition(disposition) {
  const normalized = String(disposition || "");
  const utf8Match = normalized.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const simpleMatch = normalized.match(/filename=\"?([^\";]+)\"?/i);
  return simpleMatch?.[1] || "reviews_import_template.xlsx";
}

function pillClass(prefix, tone) {
  return `${prefix} ${prefix}--${tone}`;
}

function Modal({ open, title, subtitle, onClose, children }) {
  if (!open) return null;
  return (
    <div className="ri-modalBackdrop" onClick={onClose}>
      <div className="ri-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ri-modalHeader">
          <div>
            <h2 className="ri-modalTitle">{title}</h2>
            {subtitle ? <p className="ri-modalSubtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="ri-iconBtn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="ri-modalBody">{children}</div>
      </div>
    </div>
  );
}

function ReviewImportPage() {
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [businessUnit, setBusinessUnit] = useState(null);
  const [sources, setSources] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [manualForm, setManualForm] = useState(buildManualForm());
  const [importForm, setImportForm] = useState(EMPTY_IMPORT_FORM);
  const [aiExtractionForm, setAiExtractionForm] = useState(buildAiExtractionForm());
  const [recentImportRuns, setRecentImportRuns] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [aiExtractionPreview, setAiExtractionPreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showManualModal, setShowManualModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAiExtractionModal, setShowAiExtractionModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [aiPreviewLoading, setAiPreviewLoading] = useState(false);
  const [aiImporting, setAiImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [feedback, setFeedback] = useState("");
  const effectiveBusinessUnitId = businessUnitId || String(businessUnit?.id || "");

  const fetchMeta = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const query = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : "";
      const response = await axios.get(`/api/reviews-intelligence/review-import/meta${query}`);
      const nextSources = Array.isArray(response.data?.sources) ? response.data.sources : [];
      const nextReviews = Array.isArray(response.data?.recent_reviews)
        ? response.data.recent_reviews
        : Array.isArray(response.data?.recent_manual_reviews)
          ? response.data.recent_manual_reviews
          : [];
      const nextImportRuns = Array.isArray(response.data?.recent_import_runs)
        ? response.data.recent_import_runs
        : [];
      setBusinessUnit(response.data?.business_unit || null);
      setSources(nextSources);
      setReviews(nextReviews);
      setRecentImportRuns(nextImportRuns);
      setManualForm((current) => {
        if (current.source_id && nextSources.some((source) => String(source.id) === current.source_id)) {
          return current;
        }
        return buildManualForm(nextSources);
      });
      setImportForm((current) => ({
        ...current,
        source_id:
          current.source_id && nextSources.some((source) => String(source.id) === current.source_id)
            ? current.source_id
            : nextSources[0]?.id
              ? String(nextSources[0].id)
              : "",
      }));
      setAiExtractionForm((current) => ({
        ...current,
        source_id:
          current.source_id && nextSources.some((source) => String(source.id) === current.source_id)
            ? current.source_id
            : nextSources[0]?.id
              ? String(nextSources[0].id)
              : "",
      }));
      setLoadError("");
    } catch (error) {
      setLoadError(error?.response?.data?.error || "We could not load the reviews right now.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, [businessUnitId]);

  const filteredReviews = useMemo(() => {
    const query = normalizeText(searchQuery).toLowerCase();
    return reviews.filter((review) => {
      if (sourceFilter !== "all" && String(review?.source_id) !== sourceFilter) return false;
      if (!query) return true;
      const haystack = [review?.guest_display_name, review?.source?.name, review?.title, review?.review_text, review?.positive_text, review?.negative_text]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [reviews, searchQuery, sourceFilter]);

  const stats = useMemo(() => {
    const scoreRows = reviews.filter((review) => Number.isFinite(Number(review?.normalized_rating_100)));
    const averageScore = scoreRows.length
      ? Math.round(scoreRows.reduce((sum, review) => sum + Number(review.normalized_rating_100 || 0), 0) / scoreRows.length)
      : null;
    return {
      total: reviews.length,
      averageScore,
      waitingAnalysis: reviews.filter((review) => ["pending", "processing"].includes(String(review?.ai_queue_status || "").toLowerCase())).length,
      answered: reviews.filter((review) => String(review?.reply_status || "").toLowerCase() === "responded").length,
    };
  }, [reviews]);

  const manualPreview = useMemo(() => {
    const normalizedRating = calculateNormalizedRating100(manualForm.rating_value, manualForm.rating_scale_max);
    return {
      guest: normalizeText(manualForm.guest_name) || "Anonymous guest",
      score: normalizedRating === null ? "We will calculate it automatically" : `${normalizedRating}/100`,
      reply: normalizeText(manualForm.property_reply_text) ? "Marked as already answered" : "Will appear as pending",
    };
  }, [manualForm]);

  const importPreviewRows = Array.isArray(importPreview?.preview_rows) ? importPreview.preview_rows : [];
  const importPreviewSummary = importPreview?.summary || null;
  const aiExtractionPreviewRows = Array.isArray(aiExtractionPreview?.preview_rows)
    ? aiExtractionPreview.preview_rows
    : [];
  const aiExtractionPreviewSummary = aiExtractionPreview?.summary || null;
  const aiExtractionWarnings = Array.isArray(aiExtractionPreview?.extraction?.warnings)
    ? aiExtractionPreview.extraction.warnings
    : [];

  const handleManualChange = (field) => (event) => setManualForm((current) => ({ ...current, [field]: event.target.value }));
  const handleAiExtractionChange = (field) => (event) => {
    const value = event.target.value;
    setAiExtractionForm((current) => ({ ...current, [field]: value }));
    setAiExtractionPreview(null);
  };
  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImportForm((current) => ({ ...current, file }));
    setImportPreview(null);
  };
  const resetMessages = () => {
    setActionError("");
    setFeedback("");
  };
  const openManualModal = () => {
    resetMessages();
    setManualForm((current) => (current.source_id ? current : buildManualForm(sources)));
    setShowManualModal(true);
  };
  const openImportModal = () => {
    resetMessages();
    setImportForm((current) => ({
      ...EMPTY_IMPORT_FORM,
      source_id: current.source_id || (sources[0]?.id ? String(sources[0].id) : ""),
    }));
    setImportPreview(null);
    setShowImportModal(true);
  };
  const openAiExtractionModal = () => {
    resetMessages();
    setAiExtractionForm((current) => ({
      ...EMPTY_AI_EXTRACTION_FORM,
      source_id: current.source_id || (sources[0]?.id ? String(sources[0].id) : ""),
      raw_text: current.raw_text || "",
    }));
    setAiExtractionPreview(null);
    setShowAiExtractionModal(true);
  };
  const closeModals = () => {
    setShowManualModal(false);
    setShowImportModal(false);
    setShowAiExtractionModal(false);
    setShowHelpModal(false);
    setShowAdvancedFields(false);
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setActionError("");
    setFeedback("");
    let rawPayload = null;
    try {
      rawPayload = normalizeText(manualForm.raw_payload_text) ? JSON.parse(manualForm.raw_payload_text) : null;
    } catch (_error) {
      setActionError("Advanced JSON details are not valid. Please review that field.");
      setSaving(false);
      return;
    }
    try {
      await axios.post("/api/reviews-intelligence/review-import/manual", {
        business_unit_id: effectiveBusinessUnitId,
        source_id: manualForm.source_id,
        external_review_id: manualForm.external_review_id,
        external_reservation_id: manualForm.external_reservation_id,
        guest_name: manualForm.guest_name,
        author_location: manualForm.author_location,
        original_language: manualForm.original_language,
        review_date: manualForm.review_date,
        published_at: manualForm.published_at || null,
        stay_date_from: manualForm.stay_date_from || null,
        stay_date_to: manualForm.stay_date_to || null,
        date_of_stay_text: manualForm.date_of_stay_text,
        trip_type: manualForm.trip_type,
        travel_group: manualForm.travel_group,
        title: manualForm.title,
        positive_text: manualForm.positive_text,
        negative_text: manualForm.negative_text,
        review_text: manualForm.review_text,
        property_reply_text: manualForm.property_reply_text,
        property_reply_date: manualForm.property_reply_date || null,
        rating_value: manualForm.rating_value,
        rating_scale_max: manualForm.rating_scale_max,
        raw_payload: rawPayload,
      });
      setFeedback("Review saved. The rest of the analysis will be completed automatically.");
      setManualForm(buildManualForm(sources));
      setShowManualModal(false);
      setShowAdvancedFields(false);
      await fetchMeta({ silent: true });
    } catch (error) {
      setActionError(error?.response?.data?.error || "We could not save the review.");
    } finally {
      setSaving(false);
    }
  };

  const buildImportFormData = () => {
    const formData = new FormData();
    formData.append("business_unit_id", effectiveBusinessUnitId);
    formData.append("source_id", importForm.source_id);
    if (importForm.file) {
      formData.append("file", importForm.file);
    }
    return formData;
  };

  const handleDownloadTemplate = async () => {
    if (!importForm.source_id) {
      setActionError("Choose a channel before downloading the template.");
      return;
    }

    setDownloadingTemplate(true);
    setActionError("");

    try {
      const response = await axios.get(
        `/api/reviews-intelligence/review-import/template?source_id=${encodeURIComponent(importForm.source_id)}`,
        { responseType: "blob" }
      );
      const contentType =
        response.headers?.["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const fileName = parseFileNameFromDisposition(response.headers?.["content-disposition"]);
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setActionError(error?.response?.data?.error || "We could not download the template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handlePreviewImport = async () => {
    if (!importForm.source_id || !importForm.file) {
      setActionError("Choose a channel and a file before validating the import.");
      return;
    }

    setPreviewLoading(true);
    setActionError("");
    setFeedback("");

    try {
      const response = await axios.post(
        "/api/reviews-intelligence/review-import/preview",
        buildImportFormData(),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportPreview(response.data);
    } catch (error) {
      setImportPreview(null);
      setActionError(error?.response?.data?.error || "We could not validate the import file.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImportSubmit = async (event) => {
    event.preventDefault();
    setActionError("");
    setFeedback("");

    if (!importForm.source_id || !importForm.file) {
      setActionError("Choose a channel and a file before importing.");
      return;
    }

    setImporting(true);

    try {
      const response = await axios.post(
        "/api/reviews-intelligence/review-import/file",
        buildImportFormData(),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const summary = response.data?.summary || {};
      setFeedback(
        `Import completed. ${summary.inserted_rows || 0} created, ${summary.updated_rows || 0} updated, ${summary.error_rows || 0} with issues and ${summary.skipped_rows || 0} skipped.`
      );
      setImportPreview(response.data);
      setImportForm((current) => ({ ...current, file: null }));
      await fetchMeta({ silent: true });
      setShowImportModal(false);
    } catch (error) {
      setActionError(error?.response?.data?.error || "We could not import the file.");
    } finally {
      setImporting(false);
    }
  };

  const handlePreviewAiExtraction = async () => {
    if (!aiExtractionForm.source_id || !normalizeText(aiExtractionForm.raw_text)) {
      setActionError("Choose a channel and paste the reviews before analyzing them.");
      return;
    }

    setAiPreviewLoading(true);
    setActionError("");
    setFeedback("");

    try {
      const response = await axios.post("/api/reviews-intelligence/review-import/ai-extraction/preview", {
        business_unit_id: effectiveBusinessUnitId,
        source_id: aiExtractionForm.source_id,
        raw_text: aiExtractionForm.raw_text,
      });
      setAiExtractionPreview(response.data);
    } catch (error) {
      setAiExtractionPreview(null);
      setActionError(error?.response?.data?.error || "We could not analyze the pasted reviews.");
    } finally {
      setAiPreviewLoading(false);
    }
  };

  const handleAiExtractionImport = async (event) => {
    event.preventDefault();
    setActionError("");
    setFeedback("");

    if (!aiExtractionForm.source_id || !normalizeText(aiExtractionForm.raw_text)) {
      setActionError("Choose a channel and paste the reviews before importing.");
      return;
    }

    if (!aiExtractionPreviewSummary) {
      setActionError("Analyze the pasted reviews first so you can confirm what will be imported.");
      return;
    }

    setAiImporting(true);

    try {
      const response = await axios.post("/api/reviews-intelligence/review-import/ai-extraction/import", {
        business_unit_id: effectiveBusinessUnitId,
        source_id: aiExtractionForm.source_id,
        raw_text: aiExtractionForm.raw_text,
        extracted_reviews: aiExtractionPreview?.extracted_reviews || [],
        model_name: aiExtractionPreview?.extraction?.model_name || null,
        prompt_version: aiExtractionPreview?.extraction?.prompt_version || null,
        warnings: aiExtractionWarnings,
      });
      const summary = response.data?.summary || {};
      setFeedback(
        `AI extraction completed. ${summary.inserted_rows || 0} created, ${summary.updated_rows || 0} updated, ${summary.error_rows || 0} with issues and ${summary.skipped_rows || 0} skipped.`
      );
      setAiExtractionPreview(response.data);
      setAiExtractionForm(buildAiExtractionForm(sources));
      setShowAiExtractionModal(false);
      await fetchMeta({ silent: true });
    } catch (error) {
      setActionError(error?.response?.data?.error || "We could not import the pasted reviews.");
    } finally {
      setAiImporting(false);
    }
  };

  const uiCss = `
    .ri-page{width:100%;max-width:100%;margin:0 auto;padding:0 4px 20px;display:flex;flex-direction:column;gap:18px;color:#f8fafc}
    .ri-shell{position:relative;border-radius:24px;border:1px solid rgba(255,255,255,.08);background:radial-gradient(900px 380px at 0% 0%, rgba(56,189,248,.16), transparent 58%),radial-gradient(700px 320px at 100% 0%, rgba(244,114,182,.14), transparent 52%),linear-gradient(180deg, rgba(19,23,43,.98), rgba(8,11,24,.98));box-shadow:0 28px 90px rgba(0,0,0,.45);overflow:hidden}
    .ri-shell::before{content:"";position:absolute;inset:0;background:radial-gradient(500px 220px at 10% 100%, rgba(251,191,36,.08), transparent 60%),radial-gradient(700px 260px at 60% 120%, rgba(16,185,129,.07), transparent 60%);pointer-events:none}.ri-shell>*{position:relative;z-index:1}
    .ri-hero{padding:24px 24px 18px;border-bottom:1px solid rgba(255,255,255,.08);display:flex;justify-content:space-between;gap:20px;align-items:flex-start;flex-wrap:wrap}.ri-eyebrow{display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border-radius:999px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);color:rgba(226,232,240,.92);font-size:.8rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
    .ri-title{margin:14px 0 8px;font-size:2rem;line-height:1.05;font-weight:900;letter-spacing:-.03em;color:#f8fafc}.ri-subtitle{margin:0;max-width:760px;color:rgba(226,232,240,.75);line-height:1.65;font-size:.98rem}.ri-actions{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end}
    .ri-btn{display:inline-flex;align-items:center;gap:9px;padding:11px 16px;border-radius:14px;border:1px solid transparent;cursor:pointer;font-size:.95rem;font-weight:800;transition:transform .14s ease,border-color .14s ease,background .14s ease;color:#f8fafc;background:rgba(255,255,255,.06)}.ri-btn:hover:not(:disabled){transform:translateY(-1px);border-color:rgba(255,255,255,.18)}.ri-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
    .ri-btn--primary{border-color:rgba(56,189,248,.5);background:linear-gradient(135deg, rgba(14,165,233,.24), rgba(244,114,182,.26))}.ri-btn--soft{border-color:rgba(255,255,255,.12);background:rgba(255,255,255,.05)}.ri-btn--ghost{border-color:rgba(255,255,255,.1);background:transparent}
    .ri-notice{margin:0 24px;padding:14px 16px;border-radius:16px;line-height:1.6;font-size:.92rem;border:1px solid rgba(255,255,255,.08)}.ri-notice--success{background:rgba(34,197,94,.12);color:rgba(220,252,231,.96);border-color:rgba(34,197,94,.3)}.ri-notice--error{background:rgba(239,68,68,.12);color:rgba(254,226,226,.96);border-color:rgba(239,68,68,.3)}
    .ri-content{padding:22px 24px 24px;display:flex;flex-direction:column;gap:18px}.ri-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}.ri-stat{border-radius:18px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.05);padding:18px;min-height:126px}.ri-statLabel{font-size:.82rem;text-transform:uppercase;letter-spacing:.08em;color:rgba(203,213,225,.62);font-weight:800}.ri-statValue{margin-top:10px;font-size:2.1rem;line-height:1;font-weight:900;letter-spacing:-.04em;color:#f8fafc}.ri-statHelp{margin-top:10px;color:rgba(226,232,240,.7);line-height:1.5;font-size:.9rem}
    .ri-panel{border-radius:22px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);overflow:hidden}.ri-panelHeader{padding:18px 18px 16px;border-bottom:1px solid rgba(255,255,255,.08);display:flex;justify-content:space-between;align-items:flex-end;gap:18px;flex-wrap:wrap}.ri-panelTitle{margin:0;font-size:1.25rem;font-weight:900;color:#f8fafc}.ri-panelText{margin:6px 0 0;color:rgba(226,232,240,.72);line-height:1.6;font-size:.92rem;max-width:680px}
    .ri-tools{display:flex;gap:10px;flex-wrap:wrap;align-items:center}.ri-search{min-width:260px;position:relative}.ri-search svg{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:rgba(203,213,225,.58);pointer-events:none}.ri-input,.ri-select,.ri-textarea{width:100%;border-radius:14px;border:1px solid rgba(255,255,255,.1);background:rgba(2,6,23,.5);color:#f8fafc;padding:12px 14px;outline:none;transition:border-color .14s ease,box-shadow .14s ease,background .14s ease}.ri-search .ri-input{padding-left:38px}.ri-input:focus,.ri-select:focus,.ri-textarea:focus{border-color:rgba(56,189,248,.5);box-shadow:0 0 0 4px rgba(56,189,248,.12);background:rgba(2,6,23,.62)}.ri-textarea{min-height:110px;resize:vertical}
    .ri-tableWrap{overflow:auto}.ri-table{width:100%;min-width:1080px;border-collapse:separate;border-spacing:0;table-layout:fixed}.ri-table thead th{padding:14px 18px;text-align:left;text-transform:uppercase;letter-spacing:.08em;font-size:.76rem;color:rgba(203,213,225,.58);background:rgba(8,11,24,.9);border-bottom:1px solid rgba(255,255,255,.08);position:sticky;top:0;z-index:2;white-space:normal;overflow-wrap:anywhere;word-break:break-word}.ri-table tbody td{padding:18px;border-bottom:1px solid rgba(255,255,255,.06);vertical-align:top;color:rgba(248,250,252,.92);font-size:.94rem;white-space:normal;overflow-wrap:anywhere;word-break:break-word}.ri-table tbody tr:hover td{background:rgba(255,255,255,.03)}.ri-table thead th:nth-child(1),.ri-table tbody td:nth-child(1){width:12%}.ri-table thead th:nth-child(2),.ri-table tbody td:nth-child(2){width:15%}.ri-table thead th:nth-child(3),.ri-table tbody td:nth-child(3){width:14%}.ri-table thead th:nth-child(4),.ri-table tbody td:nth-child(4){width:32%}.ri-table thead th:nth-child(5),.ri-table tbody td:nth-child(5){width:9%}.ri-table thead th:nth-child(6),.ri-table tbody td:nth-child(6){width:9%}.ri-table thead th:nth-child(7),.ri-table tbody td:nth-child(7){width:9%}
    .ri-reviewCell{min-width:0}.ri-reviewCell .ri-reviewTitle,.ri-reviewCell .ri-reviewSnippet{text-align:justify;white-space:normal;overflow-wrap:anywhere;word-break:break-word;hyphens:auto}.ri-reviewTitle{font-weight:800;color:#f8fafc;margin-bottom:6px}.ri-reviewSnippet{color:rgba(226,232,240,.74);line-height:1.55;max-width:none}.ri-mini{display:inline-flex;align-items:center;gap:8px;padding:7px 11px;border-radius:999px;font-size:.78rem;font-weight:800;border:1px solid rgba(255,255,255,.08);white-space:nowrap}.ri-mini--neutral{background:rgba(148,163,184,.12);color:rgba(226,232,240,.92);border-color:rgba(148,163,184,.24)}.ri-mini--success{background:rgba(34,197,94,.12);color:rgba(220,252,231,.96);border-color:rgba(34,197,94,.28)}.ri-mini--warning{background:rgba(245,158,11,.12);color:rgba(254,240,138,.96);border-color:rgba(245,158,11,.28)}.ri-mini--danger{background:rgba(239,68,68,.12);color:rgba(254,226,226,.96);border-color:rgba(239,68,68,.28)}.ri-mini--muted{background:rgba(71,85,105,.18);color:rgba(203,213,225,.86);border-color:rgba(100,116,139,.24)}
    .ri-meta{color:rgba(203,213,225,.68);font-size:.84rem;line-height:1.55;margin-top:4px}.ri-empty{padding:52px 26px;text-align:center;color:rgba(226,232,240,.74);line-height:1.7;font-size:.96rem}.ri-modalBackdrop{position:fixed;inset:0;background:rgba(2,6,23,.72);backdrop-filter:blur(8px);display:flex;justify-content:center;align-items:flex-start;padding:32px 18px;z-index:1200;overflow:auto}.ri-modal{width:min(980px,100%);border-radius:24px;border:1px solid rgba(255,255,255,.1);background:radial-gradient(800px 300px at 0% 0%, rgba(56,189,248,.14), transparent 58%),linear-gradient(180deg, rgba(19,23,43,.98), rgba(8,11,24,.98));box-shadow:0 28px 90px rgba(0,0,0,.5);overflow:hidden}
    .ri-modalHeader{padding:22px 22px 18px;border-bottom:1px solid rgba(255,255,255,.08);display:flex;justify-content:space-between;gap:16px;align-items:flex-start}.ri-modalTitle{margin:0;font-size:1.35rem;font-weight:900;color:#f8fafc}.ri-modalSubtitle{margin:8px 0 0;color:rgba(226,232,240,.72);line-height:1.6;font-size:.93rem;max-width:700px}.ri-modalBody{padding:22px}.ri-iconBtn{width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:#f8fafc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;flex:0 0 auto}
    .ri-form{display:flex;flex-direction:column;gap:18px}.ri-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.ri-grid--three{grid-template-columns:repeat(3,minmax(0,1fr))}.ri-field{display:flex;flex-direction:column;gap:8px}.ri-label{font-size:.82rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:rgba(203,213,225,.7)}.ri-helpText{color:rgba(203,213,225,.68);font-size:.86rem;line-height:1.55;margin-top:6px}.ri-preview{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.ri-previewCard{border-radius:18px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.05);padding:16px}.ri-previewLabel{color:rgba(203,213,225,.62);text-transform:uppercase;letter-spacing:.08em;font-size:.76rem;font-weight:800}.ri-previewValue{margin-top:10px;font-size:1rem;font-weight:800;color:#f8fafc;line-height:1.45}
    .ri-accordion{border-radius:18px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);overflow:hidden}.ri-accordionToggle{width:100%;padding:16px 18px;text-align:left;border:none;background:transparent;color:#f8fafc;font-size:.95rem;font-weight:800;cursor:pointer;display:flex;justify-content:space-between;gap:16px;align-items:center}.ri-accordionBody{padding:0 18px 18px;border-top:1px solid rgba(255,255,255,.08)}.ri-fileBox{padding:20px;border-radius:18px;border:1px dashed rgba(255,255,255,.18);background:rgba(255,255,255,.03);display:flex;flex-direction:column;gap:10px;align-items:flex-start}.ri-fileName{color:#f8fafc;font-weight:700}
    .ri-formFooter{display:flex;justify-content:space-between;gap:12px;align-items:center;flex-wrap:wrap;margin-top:4px}.ri-formFooterText{color:rgba(203,213,225,.7);font-size:.88rem;line-height:1.55;max-width:520px}.ri-helpGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.ri-helpCard{border-radius:18px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);padding:18px}.ri-helpCard h3{margin:0 0 10px;font-size:1rem;font-weight:900;color:#f8fafc}.ri-helpCard p{margin:0;color:rgba(226,232,240,.72);line-height:1.68;font-size:.92rem}
    @media (max-width:1100px){.ri-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.ri-preview{grid-template-columns:1fr}.ri-helpGrid{grid-template-columns:1fr}}@media (max-width:860px){.ri-hero,.ri-panelHeader,.ri-formFooter{flex-direction:column;align-items:stretch}.ri-stats,.ri-grid,.ri-grid--three{grid-template-columns:1fr}.ri-search{min-width:0;width:100%}.ri-actions,.ri-tools{width:100%}.ri-btn{justify-content:center}}
  `;

  return (
    <div className="ri-page">
      <style>{uiCss}</style>
      <section className="ri-shell">
        <div className="ri-hero">
          <div>
            <div className="ri-eyebrow"><FiMessageSquare />Review Intelligence</div>
            <h1 className="ri-title">Reviews received</h1>
            <p className="ri-subtitle">Keep all guest reviews in one place, add new ones when they arrive, and let the system prepare the rest for analysis. The working area is the list of reviews, not the form.</p>
            {businessUnit?.name ? <p className="ri-meta" style={{ marginTop: 12 }}>Current hotel: <strong>{businessUnit.name}</strong></p> : null}
          </div>
          <div className="ri-actions">
            <button type="button" className="ri-btn ri-btn--primary" onClick={openManualModal}><FiPlus />Add review manually</button>
            <button type="button" className="ri-btn ri-btn--soft" onClick={openImportModal}><FiUploadCloud />Import file</button>
            <button type="button" className="ri-btn ri-btn--soft" onClick={openAiExtractionModal}><FiMessageSquare />AI Extraction</button>
            <button type="button" className="ri-btn ri-btn--ghost" onClick={() => { resetMessages(); setShowHelpModal(true); }}><FiInfo />How it works</button>
          </div>
        </div>
        {feedback ? <div className="ri-notice ri-notice--success">{feedback}</div> : null}
        {loadError ? <div className="ri-notice ri-notice--error">{loadError}</div> : null}
        {actionError ? <div className="ri-notice ri-notice--error">{actionError}</div> : null}
        <div className="ri-content">
          <section className="ri-stats">
            <article className="ri-stat"><div className="ri-statLabel">Reviews on screen</div><div className="ri-statValue">{stats.total}</div><div className="ri-statHelp">The most recent reviews loaded for this hotel.</div></article>
            <article className="ri-stat"><div className="ri-statLabel">Average score</div><div className="ri-statValue">{stats.averageScore === null ? "--" : `${stats.averageScore}/100`}</div><div className="ri-statHelp">Calculated on the same scale so channels are comparable.</div></article>
            <article className="ri-stat"><div className="ri-statLabel">Waiting for analysis</div><div className="ri-statValue">{stats.waitingAnalysis}</div><div className="ri-statHelp">These reviews are already saved and will be processed next.</div></article>
            <article className="ri-stat"><div className="ri-statLabel">Already answered</div><div className="ri-statValue">{stats.answered}</div><div className="ri-statHelp">Reviews marked as already replied by the hotel.</div></article>
          </section>
          <section className="ri-panel">
            <div className="ri-panelHeader">
              <div>
                <h2 className="ri-panelTitle">Reviews list</h2>
                <p className="ri-panelText">Search, filter and review what has already entered the system. New manual reviews and future file imports will land here automatically.</p>
              </div>
              <div className="ri-tools">
                <label className="ri-search"><FiSearch /><input className="ri-input" type="text" placeholder="Search guest, channel or text" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} /></label>
                <select className="ri-select" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
                  <option value="all">All channels</option>
                  {sources.map((source) => <option key={source.id} value={String(source.id)}>{source.name}</option>)}
                </select>
                <button type="button" className="ri-btn ri-btn--ghost" onClick={() => fetchMeta({ silent: true })} disabled={refreshing || loading}><FiRefreshCw />{refreshing ? "Refreshing..." : "Refresh"}</button>
              </div>
            </div>
            <div className="ri-tableWrap">
              {loading ? <div className="ri-empty">Loading reviews...</div> : filteredReviews.length ? (
                <table className="ri-table">
                  <thead><tr><th>Date</th><th>Channel</th><th>Guest</th><th>Review</th><th>Score</th><th>Reply</th><th>Analysis</th></tr></thead>
                  <tbody>
                    {filteredReviews.map((review) => (
                      <tr key={review.id}>
                        <td><div>{formatDate(review.review_date || review.created_at)}</div><div className="ri-meta">{formatDateTime(review.created_at)}</div></td>
                        <td><div>{review?.source?.name || "Unknown channel"}</div><div className="ri-meta">{review?.manual_entry ? "Added manually" : "Imported"}</div></td>
                        <td>{review?.guest_display_name || "Anonymous guest"}</td>
                        <td className="ri-reviewCell"><div className="ri-reviewTitle">{normalizeText(review?.title) || "Guest comment"}</div><div className="ri-reviewSnippet">{buildReviewExcerpt(review)}</div></td>
                        <td><div>{formatScore(review)}</div><div className="ri-meta">{Number.isFinite(Number(review?.normalized_rating_100)) ? `${Math.round(Number(review.normalized_rating_100))}/100` : "Auto scale pending"}</div></td>
                        <td><span className={pillClass("ri-mini", getReplyTone(review?.reply_status))}>{getReplyLabel(review?.reply_status)}</span></td>
                        <td><span className={pillClass("ri-mini", getAnalysisTone(review?.ai_queue_status))}>{getAnalysisLabel(review?.ai_queue_status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="ri-empty">No reviews match the current filters.<br />Use <strong>Add review manually</strong>, <strong>Import file</strong> or <strong>AI Extraction</strong> to start feeding the list.</div>}
            </div>
          </section>
          <section className="ri-panel">
            <div className="ri-panelHeader">
              <div>
                <h2 className="ri-panelTitle">Recent imports</h2>
                <p className="ri-panelText">This helps the team confirm what was loaded recently, whether it came from a file or AI Extraction, and whether any import still needs attention.</p>
              </div>
            </div>
            <div className="ri-tableWrap">
              {recentImportRuns.length ? (
                <table className="ri-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Channel</th>
                      <th>File</th>
                      <th>Status</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentImportRuns.map((run) => (
                      <tr key={run.id}>
                        <td>
                          <div>{formatDateTime(run.started_at || run.created_at)}</div>
                        </td>
                        <td>{run?.source?.name || "Unknown channel"}</td>
                        <td>
                          <div>{run.file_name || "Manual entry"}</div>
                          <div className="ri-meta">{run.run_type || "import"}</div>
                        </td>
                        <td>
                          <span className={pillClass("ri-mini", getImportRunTone(run.status))}>
                            {getImportRunLabel(run.status)}
                          </span>
                        </td>
                        <td>
                          <div>{`${run.inserted_rows || 0} new, ${run.updated_rows || 0} updated`}</div>
                          <div className="ri-meta">{`${run.error_rows || 0} issues, ${run.skipped_rows || 0} skipped`}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="ri-empty">No imports have been run yet.</div>
              )}
            </div>
          </section>
        </div>
      </section>
      <Modal open={showManualModal} onClose={closeModals} title="Add review manually" subtitle="Use this when a guest review reaches the hotel outside the normal channel flow, or when you need to register a review from an email, survey result or screenshot.">
        <form className="ri-form" onSubmit={handleManualSubmit}>
          <div className="ri-preview">
            <div className="ri-previewCard"><div className="ri-previewLabel">Guest shown on screen</div><div className="ri-previewValue">{manualPreview.guest}</div></div>
            <div className="ri-previewCard"><div className="ri-previewLabel">Unified score</div><div className="ri-previewValue">{manualPreview.score}</div></div>
            <div className="ri-previewCard"><div className="ri-previewLabel">Reply status after saving</div><div className="ri-previewValue">{manualPreview.reply}</div></div>
          </div>
          <div className="ri-grid">
            <label className="ri-field">
              <span className="ri-label">Channel</span>
              <select className="ri-select" value={manualForm.source_id} onChange={handleManualChange("source_id")} required>
                <option value="">Choose a channel</option>
                {sources.map((source) => <option key={source.id} value={String(source.id)}>{source.name}</option>)}
              </select>
              <span className="ri-helpText">Choose where the review comes from, for example Google, Booking.com or an internal survey.</span>
            </label>
            <label className="ri-field">
              <span className="ri-label">Guest name</span>
              <input className="ri-input" type="text" value={manualForm.guest_name} onChange={handleManualChange("guest_name")} placeholder="Example: Elena" />
              <span className="ri-helpText">Write the name exactly as it arrives. If it comes without a name, you can leave it empty.</span>
            </label>
          </div>
          <div className="ri-grid ri-grid--three">
            <label className="ri-field"><span className="ri-label">Review date</span><input className="ri-input" type="date" value={manualForm.review_date} onChange={handleManualChange("review_date")} required /></label>
            <label className="ri-field"><span className="ri-label">Score</span><input className="ri-input" type="number" min="0" step="0.01" value={manualForm.rating_value} onChange={handleManualChange("rating_value")} placeholder="Example: 4" required /></label>
            <label className="ri-field"><span className="ri-label">Maximum score</span><input className="ri-input" type="number" min="1" step="0.01" value={manualForm.rating_scale_max} onChange={handleManualChange("rating_scale_max")} placeholder="Example: 5 or 10" required /></label>
          </div>
          <div className="ri-grid">
            <label className="ri-field"><span className="ri-label">Language</span><select className="ri-select" value={manualForm.original_language} onChange={handleManualChange("original_language")} required>{LANGUAGE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="ri-field"><span className="ri-label">Published time</span><input className="ri-input" type="datetime-local" value={manualForm.published_at} onChange={handleManualChange("published_at")} /><span className="ri-helpText">Optional. Use it only if the exact publication time is known.</span></label>
          </div>
          <label className="ri-field"><span className="ri-label">Review title</span><input className="ri-input" type="text" value={manualForm.title} onChange={handleManualChange("title")} placeholder="Short headline if the channel provides one" /></label>
          <div className="ri-grid">
            <label className="ri-field"><span className="ri-label">What went well</span><textarea className="ri-textarea" value={manualForm.positive_text} onChange={handleManualChange("positive_text")} placeholder="Paste the positive comments if the channel separates them" /></label>
            <label className="ri-field"><span className="ri-label">What needs improvement</span><textarea className="ri-textarea" value={manualForm.negative_text} onChange={handleManualChange("negative_text")} placeholder="Paste the negative comments if the channel separates them" /></label>
          </div>
          <label className="ri-field"><span className="ri-label">Full review</span><textarea className="ri-textarea" value={manualForm.review_text} onChange={handleManualChange("review_text")} placeholder="Paste the complete review here" /><span className="ri-helpText">At least one text area or the title should contain content so the system has something to analyze.</span></label>
          <div className="ri-grid">
            <label className="ri-field"><span className="ri-label">Trip type</span><select className="ri-select" value={manualForm.trip_type} onChange={handleManualChange("trip_type")}><option value="">Not specified</option>{TRIP_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="ri-field"><span className="ri-label">Guest profile</span><select className="ri-select" value={manualForm.travel_group} onChange={handleManualChange("travel_group")}><option value="">Not specified</option>{TRAVEL_GROUP_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          </div>
          <div className="ri-grid ri-grid--three">
            <label className="ri-field"><span className="ri-label">Stay from</span><input className="ri-input" type="date" value={manualForm.stay_date_from} onChange={handleManualChange("stay_date_from")} /></label>
            <label className="ri-field"><span className="ri-label">Stay to</span><input className="ri-input" type="date" value={manualForm.stay_date_to} onChange={handleManualChange("stay_date_to")} /></label>
            <label className="ri-field"><span className="ri-label">Stay period text</span><input className="ri-input" type="text" value={manualForm.date_of_stay_text} onChange={handleManualChange("date_of_stay_text")} placeholder="Example: March 2026" /></label>
          </div>
          <div className="ri-grid">
            <label className="ri-field"><span className="ri-label">Existing hotel reply</span><textarea className="ri-textarea" value={manualForm.property_reply_text} onChange={handleManualChange("property_reply_text")} placeholder="If the hotel has already replied, paste the message here" /></label>
            <label className="ri-field"><span className="ri-label">Reply date</span><input className="ri-input" type="datetime-local" value={manualForm.property_reply_date} onChange={handleManualChange("property_reply_date")} /></label>
          </div>
          <div className="ri-accordion">
            <button type="button" className="ri-accordionToggle" onClick={() => setShowAdvancedFields((current) => !current)}><span>Optional details for advanced cases</span><span>{showAdvancedFields ? "Hide" : "Show"}</span></button>
            {showAdvancedFields ? (
              <div className="ri-accordionBody">
                <div className="ri-grid">
                  <label className="ri-field"><span className="ri-label">External review ID</span><input className="ri-input" type="text" value={manualForm.external_review_id} onChange={handleManualChange("external_review_id")} placeholder="Original ID from the channel" /></label>
                  <label className="ri-field"><span className="ri-label">External reservation ID</span><input className="ri-input" type="text" value={manualForm.external_reservation_id} onChange={handleManualChange("external_reservation_id")} placeholder="Only if the channel provides it" /></label>
                </div>
                <div className="ri-grid">
                  <label className="ri-field"><span className="ri-label">Author location</span><input className="ri-input" type="text" value={manualForm.author_location} onChange={handleManualChange("author_location")} placeholder="Example: Rome, Italy" /></label>
                  <label className="ri-field"><span className="ri-label">Raw details in JSON</span><textarea className="ri-textarea" value={manualForm.raw_payload_text} onChange={handleManualChange("raw_payload_text")} placeholder='Optional. Example: {"platform":"google","original_payload":{}}' /><span className="ri-helpText">Use this only if you want to preserve extra technical details from the source.</span></label>
                </div>
              </div>
            ) : null}
          </div>
          <div className="ri-formFooter">
            <div className="ri-formFooterText">After saving, the hotel review is stored immediately. The system then completes the normalized score and sends it for automatic analysis in the background.</div>
            <div className="ri-actions"><button type="button" className="ri-btn ri-btn--ghost" onClick={closeModals}>Cancel</button><button type="submit" className="ri-btn ri-btn--primary" disabled={saving}>{saving ? "Saving..." : "Save review"}</button></div>
          </div>
        </form>
      </Modal>

      <Modal open={showImportModal} onClose={closeModals} title="Import reviews from a file" subtitle="Download the right template for the selected channel, upload the completed file, validate it and only then confirm the import.">
        <form className="ri-form" onSubmit={handleImportSubmit}>
          <div className="ri-grid">
            <label className="ri-field">
              <span className="ri-label">Channel</span>
              <select className="ri-select" value={importForm.source_id} onChange={(event) => { setImportForm((current) => ({ ...current, source_id: event.target.value })); setImportPreview(null); }} required>
                <option value="">Choose a channel</option>
                {sources.map((source) => <option key={source.id} value={String(source.id)}>{source.name}</option>)}
              </select>
              <span className="ri-helpText">Each channel has its own template, because the columns are not always the same.</span>
            </label>
            <div className="ri-field">
              <span className="ri-label">Step 1</span>
              <button type="button" className="ri-btn ri-btn--ghost" onClick={handleDownloadTemplate} disabled={!importForm.source_id || downloadingTemplate}>
                <FiUploadCloud />
                {downloadingTemplate ? "Downloading..." : "Download template"}
              </button>
              <span className="ri-helpText">Use this file to avoid column mistakes before importing.</span>
            </div>
          </div>

          <div className="ri-fileBox">
            <div className="ri-label">Step 2</div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
            <div className="ri-helpText">Choose the Excel or CSV file you want to validate for this hotel.</div>
            {importForm.file ? <div className="ri-fileName">Selected file: {importForm.file.name} ({Math.ceil(importForm.file.size / 1024)} KB)</div> : null}
          </div>

          <div className="ri-formFooter">
            <div className="ri-formFooterText">Validate the file first. The system will show how many rows can be created, updated or need correction before anything is saved.</div>
            <div className="ri-actions">
              <button type="button" className="ri-btn ri-btn--ghost" onClick={handlePreviewImport} disabled={!importForm.source_id || !importForm.file || previewLoading}>
                <FiSearch />
                {previewLoading ? "Validating..." : "Validate file"}
              </button>
            </div>
          </div>

          {importPreviewSummary ? (
            <>
              <div className="ri-preview">
                <div className="ri-previewCard"><div className="ri-previewLabel">Rows in file</div><div className="ri-previewValue">{importPreviewSummary.total_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Ready to create</div><div className="ri-previewValue">{importPreviewSummary.inserted_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Will update existing</div><div className="ri-previewValue">{importPreviewSummary.updated_rows || 0}</div></div>
              </div>
              <div className="ri-preview">
                <div className="ri-previewCard"><div className="ri-previewLabel">Rows with issues</div><div className="ri-previewValue">{importPreviewSummary.error_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">File checked</div><div className="ri-previewValue">{importPreview?.file_name || "Current file"}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Worksheet</div><div className="ri-previewValue">{importPreview?.sheet_name || "First sheet"}</div></div>
              </div>
              <div className="ri-tableWrap">
                <table className="ri-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Status</th>
                      <th>Guest</th>
                      <th>Review</th>
                      <th>Score</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreviewRows.map((row) => (
                      <tr key={row.row_number}>
                        <td>{row.row_number}</td>
                        <td>
                          <span className={pillClass("ri-mini", row.status === "error" ? "danger" : row.action === "update" ? "warning" : "success")}>
                            {row.status === "error" ? "Needs correction" : row.action === "update" ? "Will update" : "Ready"}
                          </span>
                        </td>
                        <td>{row.guest_name || "Anonymous guest"}</td>
                        <td>
                          <div className="ri-reviewTitle">{row.title || "Guest comment"}</div>
                          <div className="ri-reviewSnippet">{normalizeText(row.review_excerpt) || "No comment provided."}</div>
                        </td>
                        <td>{row.rating_value && row.rating_scale_max ? `${row.rating_value}/${row.rating_scale_max}` : "No score"}</td>
                        <td>
                          <div className="ri-meta">{Array.isArray(row.messages) && row.messages.length ? row.messages.join(" ") : "No issues detected."}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="ri-helpGrid">
              <article className="ri-helpCard"><h3>What the team will do here</h3><p>Select the channel, upload the file, validate the rows and only then confirm the import. The goal is to keep the workflow simple and safe.</p></article>
              <article className="ri-helpCard"><h3>Why validation matters</h3><p>The validation step catches missing dates, missing languages, invalid scores and duplicate identifiers before the file reaches the main review list.</p></article>
            </div>
          )}

          <div className="ri-formFooter">
            <div className="ri-formFooterText">If the preview shows rows with issues, the valid ones can still be imported and the problematic rows will remain logged for review.</div>
            <div className="ri-actions">
              <button type="button" className="ri-btn ri-btn--ghost" onClick={closeModals}>Close</button>
              <button type="submit" className="ri-btn ri-btn--primary" disabled={!importForm.source_id || !importForm.file || !importPreviewSummary || importing}>
                {importing ? "Importing..." : "Import now"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={showAiExtractionModal} onClose={closeModals} title="AI Extraction from pasted text" subtitle="Paste a block of reviews copied from a platform. The system will split them into individual reviews, identify the likely fields and let you confirm the result before importing.">
        <form className="ri-form" onSubmit={handleAiExtractionImport}>
          <div className="ri-grid">
            <label className="ri-field">
              <span className="ri-label">Channel</span>
              <select
                className="ri-select"
                value={aiExtractionForm.source_id}
                onChange={(event) => {
                  setAiExtractionForm((current) => ({ ...current, source_id: event.target.value }));
                  setAiExtractionPreview(null);
                }}
                required
              >
                <option value="">Choose a channel</option>
                {sources.map((source) => <option key={source.id} value={String(source.id)}>{source.name}</option>)}
              </select>
              <span className="ri-helpText">Choose the platform you copied the reviews from. This helps the extraction understand the usual layout and score style.</span>
            </label>
            <article className="ri-helpCard">
              <h3>When this works best</h3>
              <p>Paste reviews as they appear on the platform, keeping visible names, scores, dates and separators when possible. The clearer the original block, the cleaner the extraction.</p>
            </article>
          </div>

          <label className="ri-field">
            <span className="ri-label">Pasted reviews</span>
            <textarea
              className="ri-textarea"
              style={{ minHeight: 260 }}
              value={aiExtractionForm.raw_text}
              onChange={handleAiExtractionChange("raw_text")}
              placeholder="Paste here one large block copied from Google, Booking.com, Tripadvisor or another source. The system will try to split the reviews automatically."
            />
            <span className="ri-helpText">You do not need to separate the reviews manually. Paste the raw block as faithfully as possible and validate the detected reviews before importing.</span>
          </label>

          <div className="ri-formFooter">
            <div className="ri-formFooterText">First analyze the pasted text. You will see how many reviews were detected, which ones are ready to import and which ones still need attention because key data is missing.</div>
            <div className="ri-actions">
              <button
                type="button"
                className="ri-btn ri-btn--ghost"
                onClick={handlePreviewAiExtraction}
                disabled={!aiExtractionForm.source_id || !normalizeText(aiExtractionForm.raw_text) || aiPreviewLoading}
              >
                <FiSearch />
                {aiPreviewLoading ? "Analyzing..." : "Analyze pasted text"}
              </button>
            </div>
          </div>

          {aiExtractionPreviewSummary ? (
            <>
              <div className="ri-preview">
                <div className="ri-previewCard"><div className="ri-previewLabel">Reviews detected</div><div className="ri-previewValue">{aiExtractionPreviewSummary.total_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Ready to create</div><div className="ri-previewValue">{aiExtractionPreviewSummary.inserted_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Will update existing</div><div className="ri-previewValue">{aiExtractionPreviewSummary.updated_rows || 0}</div></div>
              </div>
              <div className="ri-preview">
                <div className="ri-previewCard"><div className="ri-previewLabel">Need review first</div><div className="ri-previewValue">{aiExtractionPreviewSummary.error_rows || 0}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">AI model used</div><div className="ri-previewValue">{aiExtractionPreview?.extraction?.model_name || "Configured model"}</div></div>
                <div className="ri-previewCard"><div className="ri-previewLabel">Possible warnings</div><div className="ri-previewValue">{aiExtractionWarnings.length}</div></div>
              </div>

              {aiExtractionWarnings.length ? (
                <div className="ri-helpGrid">
                  {aiExtractionWarnings.map((warning, index) => (
                    <article key={`${warning}-${index}`} className="ri-helpCard">
                      <h3>Review before importing</h3>
                      <p>{warning}</p>
                    </article>
                  ))}
                </div>
              ) : null}

              <div className="ri-tableWrap">
                <table className="ri-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Status</th>
                      <th>Guest</th>
                      <th>Review</th>
                      <th>Score</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiExtractionPreviewRows.map((row) => (
                      <tr key={row.row_number}>
                        <td>{row.row_number}</td>
                        <td>
                          <span className={pillClass("ri-mini", row.status === "error" ? "danger" : row.action === "update" ? "warning" : "success")}>
                            {row.status === "error" ? "Needs correction" : row.action === "update" ? "Will update" : "Ready"}
                          </span>
                        </td>
                        <td>{row.guest_name || "Anonymous guest"}</td>
                        <td>
                          <div className="ri-reviewTitle">{row.title || "Guest comment"}</div>
                          <div className="ri-reviewSnippet">{normalizeText(row.review_excerpt) || normalizeText(row.source_snippet) || "No comment provided."}</div>
                        </td>
                        <td>{row.rating_value && row.rating_scale_max ? `${row.rating_value}/${row.rating_scale_max}` : "No score"}</td>
                        <td>
                          <div className="ri-meta">{Array.isArray(row.messages) && row.messages.length ? row.messages.join(" ") : row.detection_notes || "No issues detected."}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="ri-helpGrid">
              <article className="ri-helpCard"><h3>Good use case</h3><p>Use this when someone copies a long list of reviews from a platform page, a partner extranet or a report where exporting to Excel is not practical.</p></article>
              <article className="ri-helpCard"><h3>What AI will do</h3><p>It will try to separate each review, detect the guest, score, dates, language and the main text, and prepare the records so they can enter the review list correctly tagged.</p></article>
              <article className="ri-helpCard"><h3>What AI will not invent</h3><p>If the pasted block does not clearly show a score, date or other key field, that review will be marked with issues instead of inventing the missing information.</p></article>
              <article className="ri-helpCard"><h3>Why the channel matters</h3><p>Choosing the source first gives the AI context about the typical review format, such as whether the platform usually uses a 5-point or 10-point score and whether it separates positives and negatives.</p></article>
            </div>
          )}

          <div className="ri-formFooter">
            <div className="ri-formFooterText">Only the valid reviews will be imported. Reviews with missing mandatory data stay flagged in the preview so the team can decide whether to clean the pasted block and try again.</div>
            <div className="ri-actions">
              <button type="button" className="ri-btn ri-btn--ghost" onClick={closeModals}>Close</button>
              <button
                type="submit"
                className="ri-btn ri-btn--primary"
                disabled={!aiExtractionForm.source_id || !normalizeText(aiExtractionForm.raw_text) || !aiExtractionPreviewSummary || aiImporting}
              >
                {aiImporting ? "Importing..." : "Import detected reviews"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={showHelpModal} onClose={closeModals} title="How this screen works" subtitle="This help is written for hotel operations. It explains what the team should do and what the system does automatically afterwards.">
        <div className="ri-helpGrid">
          <article className="ri-helpCard"><h3>When to use "Add review manually"</h3><p>Use it when a review comes by email, screenshot, phone call follow-up, or any other channel that is not yet connected automatically. Save what the guest actually said and the score they gave.</p></article>
          <article className="ri-helpCard"><h3>When to use "Import file"</h3><p>Use it when you receive many reviews together in Excel or CSV format. The import step will validate the file and create all reviews in one go without typing each one by hand.</p></article>
          <article className="ri-helpCard"><h3>When to use "AI Extraction"</h3><p>Use it when exporting is not practical and you only have a raw block copied from a website or extranet. Paste the text, validate what the system detected and import only when the preview looks right.</p></article>
          <article className="ri-helpCard"><h3>What the system fills automatically</h3><p>Once a review is saved, the system keeps the original source, prepares the guest name for display, converts the score to the common 0-100 scale and marks whether the hotel already replied or still needs to review it.</p></article>
          <article className="ri-helpCard"><h3>What appears later after analysis</h3><p>The review is sent for automatic analysis. Later the team will see suggested priority, summary, detected problems, reply guidance and operational improvement ideas.</p></article>
          <article className="ri-helpCard"><h3>What to write in the text fields</h3><p>Copy the review as faithfully as possible. If the channel splits good comments and complaints, paste them into the separate boxes. If not, the full review text is enough.</p></article>
          <article className="ri-helpCard"><h3>What not to worry about</h3><p>The team does not need to think about internal IDs, technical flags or data structure. Those details are handled in the background so the screen stays focused on day-to-day work.</p></article>
        </div>
        <div className="ri-formFooter" style={{ marginTop: 20 }}>
          <div className="ri-formFooterText">If the team needs more guidance later, this help can grow into a guided tutorial without changing the working screen.</div>
          <div className="ri-actions"><button type="button" className="ri-btn ri-btn--primary" onClick={closeModals}>Close help</button></div>
        </div>
      </Modal>
    </div>
  );
}

export default ReviewImportPage;
